import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  Avatar,
  Button,
  Divider,
  LinearProgress,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  History as HistoryIcon,
  MedicalServices as MedicalIcon,
  Warning as WarningIcon,
  LocationOn as LocationIcon,
  Timer as TimerIcon,
  Assignment as AssignmentIcon,
  LocalHospital as HospitalIcon,
} from '@mui/icons-material';

interface Appointment {
  id: number;
  bookingNumber: string;
  patientName: string;
  patientMobile: string;
  patientAge: number | null;
  patientGender: string | null;
  patientBloodGroup?: string | null;
  officerName: string;
  officerDesignation: string;
  officerDepartment: string;
  officerMobile: string;
  fullVisitAddress?: string;
  appointmentDate: string;
  appointmentTime: string;
  appointmentType: string;
  appointmentTypeDisplayName?: string;
  priority?: string;
  priorityDisplayName?: string;
  status: string;
  statusDisplayName?: string;
  reasonForVisit?: string;
  chiefComplaints?: string;
  symptoms?: string;
  specialNotes?: string;
  doctorName: string;
  doctorSpecialization: string;
  consultationFee?: number;
  visitFee?: number;
}

interface StartVisitDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  appointment: Appointment | null;
  loading?: boolean;
  doctorSpecialization?: string;
  averageConsultationMinutes?: number;
}

/**
 * Start Visit Dialog Component
 * Shows appointment details before starting visit.
 *
 * @author Civilhealth Development Team
 */
const StartVisitDialog: React.FC<StartVisitDialogProps> = ({
  open,
  onClose,
  onConfirm,
  appointment,
  loading = false,
  doctorSpecialization = 'General Medicine',
  averageConsultationMinutes = 30,
}) => {
  if (!appointment) return null;

  const expectedDuration = averageConsultationMinutes;
  const now = new Date();
  const startTime = now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  const endTime = new Date(now.getTime() + expectedDuration * 60000).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, bgcolor: 'transparent' , color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PlayIcon />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Start Visit
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {appointment.bookingNumber} - {appointment.patientName}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {loading ? (
        <Box sx={{ p: 3 }}>
          <LinearProgress />
          <Typography sx={{ mt: 2, textAlign: 'center' }}>Starting visit...</Typography>
        </Box>
      ) : (
        <DialogContent sx={{ p: 0 }}>
          {/* Appointment Start Info */}
          <Box sx={{ p: 2, bgcolor: 'transparent' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TimeIcon sx={{ color: '#2e7d32' }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Visit Start</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{startTime}</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TimerIcon sx={{ color: '#2e7d32' }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Expected Duration</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>~{expectedDuration} minutes</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MedicalIcon sx={{ color: '#2e7d32' }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Expected End</Typography>
                    <Typography variant="body2">Est. completion: {endTime}</Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Patient Info */}
          <Box sx={{ p: 2 }}>
            <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar sx={{ width: 56, height: 56, bgcolor: 'transparent' , fontSize: '1.5rem' }}>
                  {appointment.patientName?.charAt(0) || 'P'}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {appointment.patientName}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                    {appointment.patientAge && (
                      <Chip size="small" label={`${appointment.patientAge} years`} />
                    )}
                    {appointment.patientGender && (
                      <Chip size="small" label={appointment.patientGender} />
                    )}
                    {appointment.patientBloodGroup && (
                      <Chip size="small" icon={<HospitalIcon />} label={appointment.patientBloodGroup} color="error" />
                    )}
                    {appointment.priority && appointment.priority !== 'NORMAL' && (
                      <Chip size="small" label={appointment.priorityDisplayName} color="warning" />
                    )}
                  </Box>
                </Box>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Patient Mobile</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{appointment.patientMobile || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Appointment Type</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{appointment.appointmentTypeDisplayName || 'Home Visit'}</Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Chief Complaints */}
            <Paper sx={{ p: 2, borderRadius: 2, mb: 2, bgcolor: 'transparent' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <MedicalIcon fontSize="small" color="warning" />
                Chief Complaints
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {appointment.chiefComplaints || appointment.reasonForVisit || 'General Consultation'}
              </Typography>
              {appointment.symptoms && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Symptoms: {appointment.symptoms}
                </Typography>
              )}
            </Paper>

            {/* Visit Location */}
            <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationIcon fontSize="small" color="primary" />
                Visit Location
              </Typography>
              <Typography variant="body1">{appointment.fullVisitAddress || 'Address not provided'}</Typography>
            </Paper>

            {/* Officer Information */}
            <Paper sx={{ p: 2, borderRadius: 2, mb: 2, bgcolor: 'transparent' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon fontSize="small" color="primary" />
                Officer Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Name</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{appointment.officerName}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Designation</Typography>
                  <Typography variant="body2">{appointment.officerDesignation || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Department</Typography>
                  <Typography variant="body2">{appointment.officerDepartment || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Contact</Typography>
                  <Typography variant="body2">{appointment.officerMobile || 'N/A'}</Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Special Notes / Emergency */}
            {appointment.specialNotes && (
              <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningIcon fontSize="small" />
                  Important Notes
                </Typography>
                <Typography variant="body2">{appointment.specialNotes}</Typography>
              </Alert>
            )}

            {/* Quick Actions Info */}
            <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'transparent' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <AssignmentIcon fontSize="small" />
                After Starting Visit
              </Typography>
              <List dense>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <HistoryIcon fontSize="small" color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Review Patient History"
                    secondary="View past visits, prescriptions, and medical records"
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <LocationIcon fontSize="small" color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Navigate to Location"
                    secondary="Use address for GPS navigation"
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <MedicalIcon fontSize="small" color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Conduct Examination"
                    secondary="Record vitals, diagnosis, and prescriptions"
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              </List>
            </Paper>
          </Box>
        </DialogContent>
      )}

      <Divider />

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<PlayIcon />}
          onClick={onConfirm}
          disabled={loading}
          sx={{ minWidth: 150 }}
        >
          {loading ? 'Starting...' : 'Start Visit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StartVisitDialog;